import React, {
  useEffect,
  useState,
} from 'react';

import {
  PageHeader,
  StateText,
  WorkspaceHeader,
} from '../../components/Foundation';

import {
  EmptyState,
  LoadingLine,
} from '../../components/State';

import {
  getInterfaceLanguages,
} from '../learning/LiDLanguageConfig';

import './lid-v10.css';

import {
  LiDQuestionFlow,
  type LiDMode,
} from './LiDQuestionFlow';

import {
  getLidUi,
  lidBilingual,
} from './lidUi';

import {
  getLearningCopy,
} from './lidLearning';


const BUNDESLAENDER = [
  ['BW', 'Baden-Württemberg'],
  ['BY', 'Bayern'],
  ['BE', 'Berlin'],
  ['BB', 'Brandenburg'],
  ['HB', 'Bremen'],
  ['HH', 'Hamburg'],
  ['HE', 'Hessen'],
  ['MV', 'Mecklenburg-Vorpommern'],
  ['NI', 'Niedersachsen'],
  ['NW', 'Nordrhein-Westfalen'],
  ['RP', 'Rheinland-Pfalz'],
  ['SL', 'Saarland'],
  ['SN', 'Sachsen'],
  ['ST', 'Sachsen-Anhalt'],
  ['SH', 'Schleswig-Holstein'],
  ['TH', 'Thüringen'],
] as const;


const LANGUAGE_NAMES: Record<string, string> = {
  de: 'Deutsch',
  tr: 'Türkçe',
  ar: 'العربية',
  pl: 'Polski',
  uk: 'Українська',
  ru: 'Русский',
  sq: 'Shqip',
  es: 'Español',
  en: 'English',
  fa: 'فارسی',
  id: 'Bahasa Indonesia',
  fr: 'Français',
  kmr: 'Kurdî',
  ro: 'Română',
  bg: 'Български',
  it: 'Italiano',
  hr: 'Hrvatski',
  sr: 'Srpski',
  prs: 'دری',
  bs: 'Bosanski',
  vi: 'Tiếng Việt',
  hu: 'Magyar',
  el: 'Ελληνικά',
};


interface LanguageStatus {
  language: string;
  canonical: boolean;
  direction: 'ltr' | 'rtl';
  published: number;
  in_review: number;
  available: number;
}


interface Progress {
  attempted: number;
  correct: number;
  incorrect: number;
  accuracy: number;
  general_attempted: number;
  general_total: number;
  state_attempted: number;
  state_total: number;
  total_attempts: number;
  favorites: number;
  last_attempt_at: string | null;
}


type LiDAppearance = 'light' | 'dark';


export function LiDPage() {
  const [language, setLanguage] =
    useState(
      localStorage.getItem('lid.language')
      || 'de'
    );

  const [bundesland, setBundesland] =
    useState(
      localStorage.getItem('lid.bundesland')
      || 'SH'
    );

  const [appearance, setAppearance] =
    useState<LiDAppearance>(() => {
      const saved = localStorage.getItem(
        'lid.appearance'
      );

      if (saved === 'light' || saved === 'dark') {
        return saved;
      }

      return window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches
        ? 'dark'
        : 'light';
    });

  const [mode, setMode] =
    useState<LiDMode>('practice');

  const [started, setStarted] =
    useState(false);

  const [mobileSettingsOpen, setMobileSettingsOpen] =
    useState(false);

  const [openInlineSelector, setOpenInlineSelector] =
    useState<'language' | 'state' | null>(null);

  const [languages, setLanguages] =
    useState<Record<string, LanguageStatus>>({});

  const [progress, setProgress] =
    useState<Progress | null>(null);

  const [loadingMeta, setLoadingMeta] =
    useState(true);

  const [error, setError] =
    useState<string | null>(null);

  const configuredLanguages =
    getInterfaceLanguages();


  useEffect(() => {
    document.body.classList.toggle(
      'lid-light-active',
      appearance === 'light',
    );

    return () => {
      document.body.classList.remove(
        'lid-light-active'
      );
    };
  }, [appearance]);


  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const response = await fetch(
          '/api/v1/lid/languages',
          { credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error(
            `Language metadata HTTP ${response.status}`
          );
        }

        const data = await response.json();

        const map: Record<string, LanguageStatus> = {};

        for (const item of data.languages || []) {
          map[item.language] = item;
        }

        setLanguages(map);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : String(err)
        );
      } finally {
        setLoadingMeta(false);
      }
    };

    loadLanguages();
  }, []);


  const loadProgress = async () => {
    try {
      const response = await fetch(
        `/api/v1/lid/progress?bundesland=${encodeURIComponent(
          bundesland
        )}`,
        { credentials: 'include' },
      );

      if (!response.ok) {
        throw new Error(
          `Progress HTTP ${response.status}`
        );
      }

      setProgress(await response.json());
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : String(err)
      );
    }
  };


  useEffect(() => {
    if (mode === 'progress') {
      loadProgress();
    }
  }, [mode, bundesland]);


  useEffect(() => {
    if (!mobileSettingsOpen) {
      return;
    }

    const openedAt = window.scrollY;

    const handleScroll = () => {
      const distance = Math.abs(
        window.scrollY - openedAt
      );

      if (distance >= 24) {
        setMobileSettingsOpen(false);
      }
    };

    window.addEventListener(
      'scroll',
      handleScroll,
      { passive: true }
    );

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll
      );
    };
  }, [mobileSettingsOpen]);


  const changeLanguage = (
    value: string
  ) => {
    setLanguage(value);
    localStorage.setItem(
      'lid.language',
      value
    );
    setOpenInlineSelector(null);
  };


  const changeBundesland = (
    value: string
  ) => {
    setBundesland(value);
    localStorage.setItem(
      'lid.bundesland',
      value
    );
    setStarted(false);
    setMobileSettingsOpen(false);
    setOpenInlineSelector(null);
  };


  const changeAppearance = (
    value: LiDAppearance
  ) => {
    setAppearance(value);
    localStorage.setItem(
      'lid.appearance',
      value
    );
  };


  const changeMode = (
    next: LiDMode
  ) => {
    setMode(next);
    setStarted(next === 'progress');
    setMobileSettingsOpen(false);
    setError(null);
  };


  const effectiveLanguage =
    mode === 'exam' ? 'de' : language;

  const ui = getLidUi(effectiveLanguage);

  const learningCopy =
    getLearningCopy(effectiveLanguage);

  const langMeta = languages[effectiveLanguage];

  const bundeslandName =
    BUNDESLAENDER.find(
      ([code]) => code === bundesland
    )?.[1] || bundesland;

  const landingHeroImage =
    bundesland === 'SH'
      ? '/lid-visuals/optimized/states/SH/ST-SH-003.webp'
      : null;

  const modeName =
    mode === 'exam'
      ? lidBilingual('Prüfung', ui.exam, effectiveLanguage)
      : mode === 'wrong'
        ? lidBilingual('Fehler', ui.mistakes, effectiveLanguage)
        : mode === 'favorites'
          ? lidBilingual('Favoriten', ui.favorites, effectiveLanguage)
          : mode === 'progress'
            ? lidBilingual('Fortschritt', ui.progress, effectiveLanguage)
            : lidBilingual('Lernen', ui.learn, effectiveLanguage);

  return (
    <main
      lang={effectiveLanguage}
      className={`lid-page lid-theme-${appearance} ${
        started ? 'lid-session-started' : ''
      } ${
        mobileSettingsOpen
          ? 'lid-mobile-settings-open'
          : ''
      }`}
    >
      <div className="lid-intro">
        <WorkspaceHeader
          title="LiD Learn"
          description="Leben in Deutschland"
        />

        <header
          className={`lid-hero lid-landing-hero ${
            landingHeroImage ? 'has-image' : ''
          }`}
        >
          <button
            type="button"
            className="lid-hero-theme-toggle"
            aria-label={
              appearance === 'dark'
                ? learningCopy.light
                : learningCopy.dark
            }
            title={
              appearance === 'dark'
                ? learningCopy.light
                : learningCopy.dark
            }
            onClick={() =>
              changeAppearance(
                appearance === 'dark'
                  ? 'light'
                  : 'dark'
              )
            }
          >
            {appearance === 'dark' ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 15.1A8.5 8.5 0 0 1 8.9 4a8.5 8.5 0 1 0 11.1 11.1Z" />
              </svg>
            )}
          </button>
          {landingHeroImage && (
            <div
              className="lid-landing-hero-image"
              aria-hidden="true"
              style={{
                backgroundImage:
                  `url("${landingHeroImage}")`,
              }}
            />
          )}

        <div className="lid-landing-hero-copy">
          <p className="lid-eyebrow">
            Leben in Deutschland
          </p>
          <h1 className="lid-title">
            {lidBilingual(
              'Lernen. Verstehen. Bestehen.',
              ui.heroTitle,
              effectiveLanguage,
            )}
          </h1>
          <p className="lid-subtitle">
            460 {ui.questions} · {ui.translation}
            {' · '}{ui.state}
          </p>
        </div>

        <div className="lid-hero-stat">
          <strong>460</strong>
          <span>{ui.questions}</span>

          <small>
            300 {ui.general}
            {' · '}
            160 {ui.state}
          </small>
        </div>
        </header>
      </div>

      {loadingMeta && <LoadingLine />}

      {error && (
        <StateText tone="critical">
          {error}
        </StateText>
      )}

      <div className="lid-setup-controls">
        {!started && (
          <div className="lid-landing-section-heading">
            <span>
              {lidBilingual(
                'Vorbereitung',
                'Hazırlık',
                effectiveLanguage,
              )}
            </span>

            <small>
              {LANGUAGE_NAMES[effectiveLanguage]
                || effectiveLanguage}
              {' · '}
              {bundeslandName}
            </small>
          </div>
        )}

      <div className="lid-toolbar">
        <div className={`lid-field lid-morph-field ${
          openInlineSelector === 'language'
            ? 'is-open'
            : ''
        }`}>
          <span>
            {lidBilingual(
              'Sprache',
              ui.language,
              effectiveLanguage,
            )}
          </span>

          <button
            type="button"
            className="lid-morph-trigger"
            disabled={mode === 'exam'}
            aria-expanded={openInlineSelector === 'language'}
            onClick={() =>
              setOpenInlineSelector(current =>
                current === 'language'
                  ? null
                  : 'language'
              )
            }
          >
            <span className="lid-morph-value">
              {LANGUAGE_NAMES[effectiveLanguage]
                || effectiveLanguage}

              <small>
                {mode === 'exam'
                  ? ui.original
                  : languages[effectiveLanguage]?.canonical
                    ? ui.original
                    : ui.translation}
              </small>
            </span>

            <span
              className="lid-morph-orb"
              aria-hidden="true"
            >
              <svg viewBox="0 0 20 20">
                <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
              </svg>
            </span>
          </button>

          <div
            className="lid-morph-panel"
            aria-hidden={openInlineSelector !== 'language'}
          >
            <div
              className="lid-morph-options"
              role="listbox"
              aria-label={ui.language}
            >
              {configuredLanguages.map(lang => {
                const meta = languages[lang];
                const selected =
                  lang === effectiveLanguage;

                return (
                  <button
                    type="button"
                    key={lang}
                    role="option"
                    aria-selected={selected}
                    className={
                      selected
                        ? 'is-selected'
                        : ''
                    }
                    onClick={() =>
                      changeLanguage(lang)
                    }
                  >
                    <span>
                      {LANGUAGE_NAMES[lang] || lang}
                    </span>

                    <small>
                      {meta?.canonical
                        ? ui.original
                        : meta?.published === 460
                          ? ui.verified
                          : meta?.available
                            ? ui.translation
                            : ui.unavailable}
                    </small>

                    <i aria-hidden="true">
                      {selected ? '✓' : ''}
                    </i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className={`lid-field lid-morph-field ${
          openInlineSelector === 'state'
            ? 'is-open'
            : ''
        }`}>
          <span>
            {lidBilingual(
              'Bundesland',
              ui.state,
              effectiveLanguage,
            )}
          </span>

          <button
            type="button"
            className="lid-morph-trigger"
            aria-expanded={openInlineSelector === 'state'}
            onClick={() =>
              setOpenInlineSelector(current =>
                current === 'state'
                  ? null
                  : 'state'
              )
            }
          >
            <span className="lid-morph-value">
              {bundeslandName}

              <small>
                {lidBilingual(
                  'Land wählen',
                  'Eyalet seç',
                  effectiveLanguage,
                )}
              </small>
            </span>

            <span
              className="lid-morph-orb"
              aria-hidden="true"
            >
              <svg viewBox="0 0 20 20">
                <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
              </svg>
            </span>
          </button>

          <div
            className="lid-morph-panel"
            aria-hidden={openInlineSelector !== 'state'}
          >
            <div
              className="lid-morph-options lid-morph-state-options"
              role="listbox"
              aria-label={ui.state}
            >
              {BUNDESLAENDER.map(([code, name]) => {
                const selected =
                  code === bundesland;

                return (
                  <button
                    type="button"
                    key={code}
                    role="option"
                    aria-selected={selected}
                    className={
                      selected
                        ? 'is-selected'
                        : ''
                    }
                    onClick={() =>
                      changeBundesland(code)
                    }
                  >
                    <span>{name}</span>

                    <small>{code}</small>

                    <i aria-hidden="true">
                      {selected ? '✓' : ''}
                    </i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

      </div>

      {effectiveLanguage !== 'de' && langMeta && (
        <div className="lid-language-status">
          {langMeta.available}/460 {ui.translation}
          {' · '}{langMeta.published}/460 {ui.verified}
        </div>
      )}

      <div className="lid-mode-control">
        <nav
          className="lid-mode-nav"
          aria-label={ui.learn}
        >
          <button
            type="button"
            className={`lid-mode-button ${mode === 'practice' ? 'is-active' : ''}`}
            onClick={() =>
              changeMode('practice')
            }
          >
            {lidBilingual('Lernen', ui.learn, effectiveLanguage)}
          </button>

          <button
            type="button"
            className={`lid-mode-button ${mode === 'exam' ? 'is-active' : ''}`}
            onClick={() =>
              changeMode('exam')
            }
          >
            {lidBilingual('Prüfung', ui.exam, effectiveLanguage)} · 33
          </button>

          <button
            type="button"
            className={`lid-mode-button ${mode === 'wrong' ? 'is-active' : ''}`}
            onClick={() =>
              changeMode('wrong')
            }
          >
            {lidBilingual('Fehler', ui.mistakes, effectiveLanguage)}
          </button>

          <button
            type="button"
            className={`lid-mode-button ${mode === 'favorites' ? 'is-active' : ''}`}
            onClick={() =>
              changeMode('favorites')
            }
          >
            {lidBilingual('Favoriten', ui.favorites, effectiveLanguage)}
          </button>

          <button
            type="button"
            className={`lid-mode-button ${mode === 'progress' ? 'is-active' : ''}`}
            onClick={() =>
              changeMode('progress')
            }
          >
            {lidBilingual('Fortschritt', ui.progress, effectiveLanguage)}
          </button>
        </nav>

        {mode !== 'progress' && !started && (
          <div className="lid-mode-action-compact">
            <span className="lid-mode-action-summary">
              {mode === 'exam'
                ? `33 ${ui.questions} · 30 ${ui.general} + 3 ${ui.state}`
                : mode === 'wrong'
                  ? `${ui.incorrect} · ${ui.directFeedback}`
                  : mode === 'favorites'
                    ? `${ui.favorites} · ${ui.learn}`
                    : `460 ${ui.questions} · ${ui.directFeedback}`}
            </span>

            <button
              type="button"
              className="lid-mode-action-button compact"
              aria-label={`${modeName} ${ui.start}`}
              onClick={() => {
                setStarted(true);
                setMobileSettingsOpen(false);
              }}
            >
              {ui.start}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        )}
      </div>
      </div>

      {started && mode !== 'progress' && (
        <div className="lid-mobile-session-header">
          <div className="lid-mobile-session-summary">
            <strong>
              {mode === 'exam'
                ? `${LANGUAGE_NAMES.de} · ${ui.original}`
                : LANGUAGE_NAMES[effectiveLanguage]
                  || effectiveLanguage}
            </strong>
            <span>·</span>
            <span>{bundeslandName}</span>
            <span>·</span>
            <span>{modeName}</span>
          </div>

          <button
            type="button"
            className="lid-mobile-settings-button"
            onClick={() =>
              setMobileSettingsOpen(
                value => !value
              )
            }
            aria-expanded={mobileSettingsOpen}
          >
            {mobileSettingsOpen
              ? ui.close
              : ui.settings}
          </button>
        </div>
      )}

      

      {mode !== 'progress' && started && (
        <LiDQuestionFlow
          bundesland={bundesland}
          language={effectiveLanguage}
          mode={mode}
          onProgressChanged={
            loadProgress
          }
        />
      )}

      {mode === 'progress' && (
        <section className="lid-progress-panel">
          <PageHeader
            title={modeName}
          />

          {!progress && <LoadingLine />}

          {progress && (
            <div className="lid-progress-grid">
              <div className="lid-stat-card">
                <span>{ui.attempted}</span>
                <strong>{progress.attempted}</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.correct}</span>
                <strong>{progress.correct}</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.incorrect}</span>
                <strong>{progress.incorrect}</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.accuracy}</span>
                <strong>{progress.accuracy}%</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.general}</span>
                <strong>{progress.general_attempted}/300</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.state}</span>
                <strong>{progress.state_attempted}/10</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.favorites}</span>
                <strong>{progress.favorites}</strong>
              </div>

              <div className="lid-stat-card">
                <span>{ui.totalAnswers}</span>
                <strong>{progress.total_attempts}</strong>
              </div>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
